import { isNumber } from "lodash";

import { percentToDecimal } from "@citrine/utils/number";
import {
  isGreaterThanOrEqualTo,
  isLessThanOrEqualTo,
} from "@citrine/utils/validation";

import type { ComboCandidate } from "../../apex";
import type { MixtureCandidateValue } from "../../execution-candidate";
import {
  isCategoricalCandidateValue,
  isMixtureCandidateValue,
  isRealCandidateValue,
} from "../../execution-candidate";

import type {
  CategoricalDescriptorFilter,
  FilterFunction,
  FilterGroup,
  FormulationFilter,
  IngredientCountFilter,
  IngredientCountSubfilter,
  IngredientFilter,
  LabelFilter,
  PerformanceFilter,
  ProcessFilter,
  RealDescriptorFilter,
} from "./types";
import { FORMULATION_OPERATION } from "./types";

const createRangeFilterFunction = (min?: number, max?: number) => {
  const lowerBoundCheck = isGreaterThanOrEqualTo(min ?? NaN);
  const upperBoundCheck = isLessThanOrEqualTo(max ?? NaN);

  return (value: number) =>
    isNumber(value) ? lowerBoundCheck(value) && upperBoundCheck(value) : false;
};

/**
 *
 * @param candidateValues
 * @returns a number for the total with a fall through case of just returning 0 for the total
 */
const getMixtureCandidateTotalValue = (
  candidateValues: MixtureCandidateValue,
): number => {
  return (Object.values(candidateValues.q) as number[]).reduce(
    (sum, current) => {
      return sum + current;
    },
    0,
  );
};

/**
 * Takes an operation and a set of filter functions, and returns a single filter
 * function that leverages either `every` (and) or `some` (or).
 */
const joinFilterFunctions = (
  operation: "and" | "or",
  filterFunctions: FilterFunction[],
): FilterFunction => {
  if (filterFunctions.length) {
    switch (operation) {
      case "and":
        return (candidate: ComboCandidate) =>
          filterFunctions.every((filterFunction) => filterFunction(candidate));
      case "or":
        return (candidate: ComboCandidate) =>
          filterFunctions.some((filterFunction) => filterFunction(candidate));
    }
  }
  // this is a good catch for the case where we have no filters below and should let candidates pass if there are no sub filters to exclude by
  return () => true;
};

//
// ====================
//  Ingredient methods
// ====================
//

const buildIngredientSubFilter = (
  filter: FormulationFilter,
): FilterFunction => {
  /**
   * Ingredients are found under:
   * the candidate has structure of {min, max?}, {min?, max} or {min, max}
   * special case we want to cover is if we have a minumum of 0
   * * we want to pass the candidate value even if they key is not present in the candidate
   */
  const rangeFilter = createRangeFilterFunction(filter.min, filter.max);

  const filterFunctions = filter.terms.map((value) => {
    return (candidate: ComboCandidate) => {
      const candidateValues = candidate.material.vars?.[filter.formulation];
      if (!isMixtureCandidateValue(candidateValues)) {
        return false;
      }
      const totalValue = getMixtureCandidateTotalValue(candidateValues);
      const candidateValue = candidateValues.q[value];
      // if we have a  maximum set to 0 and we have a candidate with a missing key we'll pass it as true
      if (filter.max === 0 && !isNumber(candidateValue)) {
        return true;
      }
      if (!isNumber(candidateValue)) {
        return false;
      }
      return rangeFilter(candidateValue / totalValue);
    };
  });

  return joinFilterFunctions(FORMULATION_OPERATION, filterFunctions);
};

const buildIngredientFilter = (filter: IngredientFilter): FilterFunction => {
  const filterFunctions = filter.terms.map(buildIngredientSubFilter);
  return joinFilterFunctions(filter.operation, filterFunctions);
};

//
// ==========================
//  Ingredient Count methods
// ==========================
//

const buildIngredientCountSubfilter = (
  filter: IngredientCountSubfilter,
): FilterFunction => {
  const rangeFilter = createRangeFilterFunction(filter.min, filter.max);

  return (candidate: ComboCandidate) => {
    const mixtureValue = candidate.material.vars?.[filter.formulation];
    if (!isMixtureCandidateValue(mixtureValue)) {
      return false;
    }
    const ingredientCount = Object.values(mixtureValue.q).filter(
      Boolean,
    ).length;
    return rangeFilter(ingredientCount);
  };
};

const buildIngredientCountFilter = (
  filter: IngredientCountFilter,
): FilterFunction => {
  const filterFunctions = filter.terms.map(buildIngredientCountSubfilter);
  return joinFilterFunctions(filter.operation, filterFunctions);
};

//
// ===============
//  Label methods
// ===============
//

const buildLabelSubFilter = (filter: FormulationFilter): FilterFunction => {
  /**
   * this feels like a modification of the buildIngredientSubFilter
   * the main difference is that we use the label to pull back set of filters
   * and just add those values together to cum up with our "candidateValue"
   */
  const rangeFilter = createRangeFilterFunction(filter.min, filter.max);

  const filterFunctions = filter.terms.map((value) => {
    return (candidate: ComboCandidate) => {
      const candidateValues = candidate.material.vars?.[filter.formulation];

      if (!isMixtureCandidateValue(candidateValues)) {
        return false;
      }

      const labelIngredients = candidateValues.l[value];

      if (filter.max === 0 && !Array.isArray(labelIngredients)) {
        return true;
      }
      if (!Array.isArray(labelIngredients)) {
        return false;
      }

      const totalValue = getMixtureCandidateTotalValue(candidateValues);

      const candidateValue = labelIngredients.reduce((sum, current) => {
        const currentValue = candidateValues.q[current];
        return isNumber(currentValue) ? sum + currentValue : sum;
      }, 0);

      return rangeFilter(candidateValue / totalValue);
    };
  });
  return joinFilterFunctions(FORMULATION_OPERATION, filterFunctions);
};

const buildLabelFilter = (filter: LabelFilter): FilterFunction => {
  const filterFunctions = filter.terms.map(buildLabelSubFilter);
  return joinFilterFunctions(filter.operation, filterFunctions);
};

//
// =============================
//  Performance/Process methods
// =============================
//

const buildCategoricalMostLikelyFilter = (
  filter: CategoricalDescriptorFilter,
): FilterFunction => {
  return (candidate: ComboCandidate) => {
    const candidateValues = candidate.material.vars?.[filter.descriptor_key];

    if (!isCategoricalCandidateValue(candidateValues)) {
      return false;
    }

    const maxValue = Math.max(
      ...(Object.values(candidateValues.cp) as number[]),
    );

    // Use `some` because Categorical filter terms are implicitly OR'd
    //
    // Returns true if any of the filter's values have the highest probability
    // in the candidate value
    const satisfiesMostLikely = filter.values.some((category) => {
      const candidateProbability = candidateValues.cp[category];
      return isNumber(candidateProbability) && candidateProbability >= maxValue;
    });

    // If `exclude` is true, we ignore candidates that satisfy the above criteria
    return filter.exclude ? !satisfiesMostLikely : satisfiesMostLikely;
  };
};

const buildCategoricalProbabilityFilter = (
  filter: CategoricalDescriptorFilter,
  minProbability: number,
): FilterFunction => {
  return (candidate: ComboCandidate) => {
    const candidateValues = candidate.material.vars?.[filter.descriptor_key];

    if (!isCategoricalCandidateValue(candidateValues)) {
      return false;
    }

    // Use `some` because Categorical filter terms are implicitly OR'd
    //
    // Returns true if any of the filter's values in the candidate value are
    // greater than or equal to the filter's mininum probability
    const satisfiesProbability = filter.values.some((valueKey) => {
      const candidateProbability = candidateValues.cp[valueKey];

      // if we have a minimum or maximum set to 0 and we have a candidate with a missing key we'll pass it as true
      if (minProbability === 0 && !isNumber(candidateProbability)) {
        return true;
      }

      if (!isNumber(candidateProbability)) {
        return false;
      }

      return candidateProbability >= minProbability;
    });

    // If `exclude` is true, we ignore candidates that satisfy the above criteria
    return filter.exclude ? !satisfiesProbability : satisfiesProbability;
  };
};

const buildCategoricalDescriptorFilter = (
  filter: CategoricalDescriptorFilter,
): FilterFunction => {
  if (filter.most_likely) {
    return buildCategoricalMostLikelyFilter(filter);
  } else {
    return buildCategoricalProbabilityFilter(
      filter,
      percentToDecimal(filter.probability.min_percent),
    );
  }
};

const buildRealDescriptorFilter = (
  filter: RealDescriptorFilter,
): FilterFunction => {
  /**
   * RealDescriptionFilter has a descriptor key for the property to evaluate.
   * It has a range property with {min, max}, {min, max?} or {min?, max}. that denotes an inclusive limiting value.
   */
  const rangeFilter = createRangeFilterFunction(filter.min, filter.max);

  return (candidate: ComboCandidate) => {
    const candidateValue = candidate.material.vars?.[filter.descriptor_key];
    if (!isRealCandidateValue(candidateValue)) {
      return false;
    }
    return rangeFilter(candidateValue.m);
  };
};

const buildPerformanceOrProcessFilter = (
  filter: PerformanceFilter | ProcessFilter,
): FilterFunction => {
  const filterFunctions = filter.terms.map((term) => {
    switch (term.type) {
      case "CategoricalFilter":
        return buildCategoricalDescriptorFilter(term);
      case "RealFilter":
        return buildRealDescriptorFilter(term);
    }
  });

  return joinFilterFunctions(filter.operation, filterFunctions);
};

//
// ===============
//  Entry methods
// ===============
//

const buildFilteringFunction = (filterGroup: FilterGroup): FilterFunction => {
  const filterFunctions = filterGroup.terms.map((term) => {
    switch (term.type) {
      case "IngredientFilter":
        return buildIngredientFilter(term);
      case "IngredientCountFilter":
        return buildIngredientCountFilter(term);
      case "LabelFilter":
        return buildLabelFilter(term);
      case "PerformanceFilter":
      case "ProcessFilter":
        return buildPerformanceOrProcessFilter(term);
    }
  });
  return joinFilterFunctions(filterGroup.operation, filterFunctions);
};

export const filterCandidates = (
  candidates: ComboCandidate[],
  filterGroup: FilterGroup,
) => {
  const filteringFunction = buildFilteringFunction(filterGroup);
  return candidates.filter(filteringFunction);
};
